import {Table, Tag, Tooltip, Dropdown, Modal} from "antd";
import dayjs from "dayjs";
import {Button, Icon} from "~/components";

const STATUS_MAP = {
	upcoming:    {label: 'Sắp diễn ra',  color: 'processing'},
	in_progress: {label: 'Đang diễn ra', color: 'blue'},
	overdue:     {label: 'Quá giờ',      color: 'orange'},
	trained:     {label: 'Đã đào tạo',   color: 'success'},
	not_trained: {label: 'Chưa đào tạo', color: 'error'},
	postponed:   {label: 'Đời lịch',     color: 'warning'},
	cancelled:   {label: 'Đã hủy',       color: 'default'},
};

const TYPE_LABELS = {
	all:      'Toàn bộ phòng KD',
	by_group: 'Theo phòng',
};

function formatDateTime(ts) {
	if (!ts) return '—';
	return dayjs.unix(ts).format('DD/MM/YYYY HH:mm');
}

function confirm(title, onOk, okText = 'Xác nhận', danger = false) {
	Modal.confirm({
		title,
		okText,
		cancelText:     'Hủy',
		okButtonProps:  {danger},
		onOk,
	});
}

function TrainingScheduleTable({
	items,
	pagination,
	onPaginationChange,
	setItemEdit,
	openModal,
	onStatusChange,
	canEdit,
	canDelete,
	canChangeStatus,
	currentUserId,
}) {

	const buildMenuItems = (record) => {
		const ds           = record.displayStatus || {};
		const storedStatus = record.status;
		const isTrainer    = currentUserId && String(record.trainerId) === String(currentUserId);
		const canEditRow   = canEdit
			&& !['trained', 'not_trained', 'cancelled'].includes(storedStatus)
			&& ds.key !== 'in_progress';

		const trainerCanConfirm = isTrainer
			&& ['overdue', 'in_progress', 'upcoming'].includes(ds.key)
			&& !['cancelled', 'postponed'].includes(storedStatus);

		const items = [];

		if (canEditRow) {
			items.push({
				key:   'edit',
				label: 'Chỉnh sửa',
				icon:  <span className="icon">{Icon.edit}</span>,
				onClick: () => { setItemEdit(record); openModal('addEdit'); },
			});
		}

		if (trainerCanConfirm) {
			items.push({type: 'divider'});
			items.push({
				key:   'trained',
				label: 'Đã đào tạo',
				icon:  <span className="icon" style={{color: '#52c41a'}}>{Icon.success}</span>,
				onClick: () => confirm('Xác nhận đã tổ chức đào tạo?', () => onStatusChange(record, 'trained'), 'Đã đào tạo'),
			});
			items.push({
				key:     'not_trained',
				label:   'Chưa đào tạo',
				danger:  true,
				icon:    <span className="icon">{Icon.close}</span>,
				onClick: () => confirm('Xác nhận chưa tổ chức đào tạo?', () => onStatusChange(record, 'not_trained'), 'Chưa đào tạo', true),
			});
		}

		const canPostponeCancel = canChangeStatus || isTrainer;

		if (canPostponeCancel && storedStatus === 'upcoming' && ds.key !== 'in_progress') {
			items.push({type: 'divider'});
			items.push({
				key:   'postponed',
				label: 'Đời lịch',
				icon:  <span className="icon">{Icon.hourglass}</span>,
				onClick: () => confirm('Đời lịch đào tạo này?', () => onStatusChange(record, 'postponed')),
			});
		}

		if (canPostponeCancel && ['upcoming', 'postponed'].includes(storedStatus)) {
			items.push({
				key:     'cancelled',
				label:   'Hủy lịch',
				danger:  true,
				icon:    <span className="icon">{Icon.close}</span>,
				onClick: () => confirm('Hủy lịch đào tạo này?', () => onStatusChange(record, 'cancelled'), 'Hủy lịch', true),
			});
		}

		if (canDelete) {
			items.push({type: 'divider'});
			items.push({
				key:     'delete',
				label:   'Xóa',
				danger:  true,
				icon:    <span className="icon">{Icon.delete}</span>,
				onClick: () => { setItemEdit(record); openModal('delete'); },
			});
		}

		return items;
	};

	const columns = [
		{
			title: 'STT',
			key:   'stt',
			width: 55,
			render: (_, __, index) => (pagination.page - 1) * pagination.limit + index + 1,
		},
		{
			title:  'Chủ đề đào tạo',
			key:    'topic',
			render: (_, record) => {
				const isToday = record.startTime && dayjs.unix(record.startTime).isSame(dayjs(), 'day');
				return (
					<div style={{display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'}}>
						{isToday && (
							<Tag color="volcano" style={{fontSize: 11, margin: 0, flexShrink: 0}}>Hôm nay</Tag>
						)}
						<span style={{fontWeight: isToday ? 600 : 400}}>{record.topic}</span>
					</div>
				);
			},
		},
		{
			title:  'Người đào tạo',
			key:    'trainer',
			width:  200,
			render: (_, record) => record.trainer
				? `${record.trainer.firstname} ${record.trainer.lastname}`
				: '—',
		},
		{
			title:  'Bắt đầu',
			key:    'startTime',
			width:  200,
			render: (_, record) => formatDateTime(record.startTime),
		},
		{
			title:  'Kết thúc',
			key:    'endTime',
			width:  200,
			render: (_, record) => formatDateTime(record.endTime),
		},
		{
			title:  'Loại',
			key:    'type',
			width:  200,
			render: (_, record) => {
				if (record.type === 'by_group' && record.groups?.length) {
					return (
						<Tooltip title={record.groups.map(g => g.name).join(', ')}>
							<span style={{cursor: 'help'}}>Theo phòng ({record.groups.length})</span>
						</Tooltip>
					);
				}
				return TYPE_LABELS[record.type] || record.type;
			},
		},
		{
			title:  'Trạng thái',
			key:    'status',
			width:  200,
			render: (_, record) => {
				const ds  = record.displayStatus || {};
				const cfg = STATUS_MAP[ds.key] || {label: ds.label || record.status, color: 'default'};
				return <Tag color={cfg.color}>{cfg.label}</Tag>;
			},
		},
		{
			title: '',
			key:   'action',
			width: 60,
			render: (_, record) => {
				const menuItems = buildMenuItems(record);
				if (!menuItems.filter(i => i.type !== 'divider').length) return null;
				return (
					<Dropdown menu={{items: menuItems}} trigger={['click']} placement="bottomRight">
						<Button background small leftIcon={Icon.menuVertical} />
					</Dropdown>
				);
			},
		},
	];

	return (
		<>
			<Table
				columns={columns}
				dataSource={[...items].sort((a, b) => {
					const today = dayjs();
					const aT = a.startTime && dayjs.unix(a.startTime).isSame(today, 'day');
					const bT = b.startTime && dayjs.unix(b.startTime).isSame(today, 'day');
					if (aT && !bT) return -1;
					if (!aT && bT) return 1;
					return 0;
				})}
				rowKey="id"
				onRow={(record) => {
					const isToday = record.startTime && dayjs.unix(record.startTime).isSame(dayjs(), 'day');
					return isToday ? {style: {background: '#fffbeb'}} : {};
				}}
				pagination={{
					current:   pagination.page,
					pageSize:  pagination.limit,
					total:     pagination.total,
					showTotal: (total) => `Tổng ${total} lịch đào tạo`,
				}}
				onChange={(pag) => { if (onPaginationChange) onPaginationChange(pag.current); }}
				scroll={{x: 900}}
			/>
		</>
	);
}

export default TrainingScheduleTable;
